import { apiFactory, Request, type Settings } from '@app/client';
import {
    type AccountAddResponse,
    type Mutation,
    type MutationAccountAddArgs,
    type MutationAccountRemoveArgs,
    type Query,
    type QueryAccountsAllByUserIdArgs,
} from '@app/graphql/codegen/graphql';
import accountAddMutation from '@app/graphql/mutations/accountAdd.graphql';
import accountRemoveMutation from '@app/graphql/mutations/accountRemove.graphql';
import accountVerifyMutation from '@app/graphql/mutations/accountVerify.graphql';
import accountsByUser from '@app/graphql/queries/accountsByUser.graphql';
import { setError } from '@app/store/actions/error';

type WalletByProps = {
    settings?: Settings;
    variables: QueryAccountsAllByUserIdArgs;
};

type WalletAddProps = {
    settings?: Settings;
    variables: MutationAccountAddArgs;
};

type WalletRemoveProps = {
    settings?: Settings;
    variables: MutationAccountRemoveArgs;
};

export async function addWallet(params: WalletAddProps): Promise<Mutation> {
    return Request.graphqlMutation({
        ...params,
        query: accountAddMutation,
        apiSettingsType: 'marketplace',
    });
}

// TODO: `params` should be typed (CallAddWalletParams)
export async function callAddWallet(params: WalletAddProps): Promise<AccountAddResponse> {
    return apiFactory()
        .graphqlCall(addWallet, params)
        .then((response) => {
            if (!response) throw new Error();
            return response.accountAdd;
        })
        .catch((error) => {
            setError({
                ...error,
                log: true,
                source: 'Error adding new wallet.',
            })();

            return error;
        });
}
interface RemoveWalletRes extends Mutation {
    data?: { accountRemove?: boolean };
}
export async function removeWallet(params: WalletRemoveProps): Promise<RemoveWalletRes> {
    return Request.graphqlMutation({
        ...params,
        query: accountRemoveMutation,
        apiSettingsType: 'marketplace',
    });
}

export async function callRemoveWallet(params: WalletRemoveProps) {
    return apiFactory()
        .graphqlCall(removeWallet, params)
        .then((response) => {
            if (!response) throw new Error();
            return response;
        });
}

export async function verifyWallet(params: WalletByProps): Promise<Mutation> {
    return Request.graphqlMutation({
        ...params,
        query: accountVerifyMutation,
        apiSettingsType: 'marketplace',
    });
}

// TODO: `params` should be typed (CallVerifyWalletParams)
export async function callVerifyWallet(params): Promise<boolean> {
    return apiFactory()
        .graphqlCall(verifyWallet, params)
        .then((response) => {
            if (!response) throw new Error();
            return response.accountVerify;
        })
        .catch((error) => {
            setError({
                ...error,
                log: true,
                source: 'Error verifying new wallet.',
            })();

            return error;
        });
}

async function getConnectedWalletsByUser(params: WalletByProps): Promise<Query> {
    return Request.graphqlQuery({
        ...params,
        query: accountsByUser,
        apiSettingsType: 'marketplace',
    });
}

export const callGetConnectedWalletsByUser = async (params: WalletByProps) => {
    return (
        apiFactory()
            .graphqlCall(getConnectedWalletsByUser, params)
            .then((response) => {
                if (!response) throw new Error();
                return response.accountsAllByUserId;
            })
            // eslint-disable-next-line no-console
            .catch((error) => console.error(error))
    );
};
